// crewbit-admin/src/components/schedule/WalkInQueueCard.tsx
//
// Schedule Module Phase C C5A — pending walk-in queue card (admin).
// Mirror of crewbit-mobile/components/schedule/WalkInQueueCard.tsx; structure
// parallel modulo runtime per feedback_admin_pair_must_match_mobile_body.md
// (RN StyleSheet → inline CSS; useDraggable wired here for V-W3-FULL drag-drop
// onto operator columns; mobile is read-only display).
//
// Spec: docs/references/schedule/SCHEDULE_MODULE_SPEC_v1.md §10. Business-pref-gated
// overlay rendered when `business_schedule_preferences.walk_in_enabled=true`.
//
// Drag-drop wiring: each <DraggableWalkInTile> calls @dnd-kit useDraggable with
// `data: { kind: 'walk_in', walkIn }`. Schedule index onDragEnd discriminates
// via `active.data.current.kind` and routes to handleWalkInAssign on drop into
// `day-col-{userId}` / `grid-col-{userId}` targets. Pattern mirrors
// UnassignedBar precedent.

import { useCallback, useState } from 'react';
import { useDraggable } from '@dnd-kit/core';
import { ChevronDown, ChevronRight, Plus, UserPlus } from 'lucide-react';
import { palette, shadow } from '../../styles/tokens';
import { useCreateWalkInPending } from '../../hooks/useCreateWalkInPending';
import { useWalkInQueue, type PendingWalkIn } from '../../hooks/useWalkInQueue';
import { lookupErrorCopy } from '../../lib/errorMap';

export interface WalkInQueueCardProps {
  businessId: string;
  /** Optional: telemetry hook fired after a successful add. */
  onAddSuccess?: (walkInId: string, alreadyCreated: boolean) => void;
}

function DraggableWalkInTile({ walkIn, label }: { walkIn: PendingWalkIn; label: string }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: walkIn.id,
    data: { kind: 'walk_in', walkIn },
  });

  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      style={{
        minWidth: 160,
        padding: '8px 10px',
        borderRadius: 6,
        cursor: isDragging ? 'grabbing' : 'grab',
        background: palette.amberWash,
        border: `1px solid ${palette.amber}30`,
        borderLeft: `3px solid ${palette.amber}`,
        flexShrink: 0,
        transition: 'border-color 0.12s, opacity 0.15s',
        opacity: isDragging ? 0.3 : 1,
        touchAction: 'none',
      }}
    >
      <div style={{ fontSize: 9, fontWeight: 600, color: palette.amber, textTransform: 'uppercase', marginBottom: 2 }}>
        Walk-in
      </div>
      <div style={{ fontSize: 12, fontWeight: 600, color: palette.g800, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {label}
      </div>
      {walkIn.client_phone ? (
        <div style={{ fontSize: 10, color: palette.g500 }}>{walkIn.client_phone}</div>
      ) : null}
      <div style={{ fontSize: 10, color: palette.amber, fontWeight: 600, marginTop: 2 }}>Drag to operator</div>
    </div>
  );
}

export function WalkInQueueCard({ businessId, onAddSuccess }: WalkInQueueCardProps) {
  const { walkIns, loading, error } = useWalkInQueue(businessId);
  const createWalkIn = useCreateWalkInPending();
  const [expanded, setExpanded] = useState(true);
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);

  const handleAdd = useCallback(async () => {
    if (adding) return;
    setAdding(true);
    setAddError(null);
    const idempotencyKey = (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function')
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const result = await createWalkIn({ businessId, idempotencyKey });
    setAdding(false);
    if (!result.success) {
      setAddError(lookupErrorCopy('create_walk_in_pending', result.error.code));
      return;
    }
    onAddSuccess?.(result.data.walk_in_id, Boolean(result.data.already_created));
  }, [adding, businessId, createWalkIn, onAddSuccess]);

  return (
    <div
      style={{
        marginBottom: 12,
        background: palette.white,
        borderRadius: 10,
        border: `1px solid ${palette.g150}`,
        overflow: 'hidden',
        boxShadow: shadow.card,
      }}
    >
      <button
        onClick={() => setExpanded((v) => !v)}
        style={{
          display: 'flex', alignItems: 'center', gap: 6, width: '100%', padding: '10px 14px',
          border: 'none', background: 'transparent', cursor: 'pointer', fontFamily: 'inherit',
        }}
        aria-label="Toggle walk-in queue"
      >
        {expanded ? <ChevronDown size={14} color={palette.g500} /> : <ChevronRight size={14} color={palette.g500} />}
        <UserPlus size={14} color={palette.g700} />
        <span style={{ fontSize: 13, fontWeight: 600, color: palette.g700, flex: 1, textAlign: 'left' }}>
          Walk-in queue
        </span>
        <span
          style={{
            padding: '2px 8px',
            borderRadius: 10,
            fontSize: 11,
            fontWeight: 600,
            background: walkIns.length > 0 ? palette.amberWash : palette.g100,
            color: walkIns.length > 0 ? palette.amber : palette.g500,
          }}
        >
          {walkIns.length}
        </span>
      </button>

      {expanded ? (
        <div style={{ padding: '0 14px 12px', display: 'flex', flexDirection: 'column', gap: 8 }}>
          {error ? (
            <div style={{ fontSize: 12, color: palette.red }}>{error}</div>
          ) : null}

          {loading && walkIns.length === 0 ? (
            <div style={{ fontSize: 12, color: palette.g500, fontStyle: 'italic' }}>Loading…</div>
          ) : walkIns.length === 0 ? (
            <div style={{ fontSize: 12, color: palette.g500, fontStyle: 'italic' }}>No walk-ins waiting.</div>
          ) : (
            <div style={{ display: 'flex', gap: 8, overflowX: 'auto' }}>
              {walkIns.map((w, idx) => (
                <DraggableWalkInTile
                  key={w.id}
                  walkIn={w}
                  label={w.client_name || `Walk-in #${idx + 1}`}
                />
              ))}
            </div>
          )}

          <button
            onClick={handleAdd}
            disabled={adding}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              padding: '8px 12px',
              borderRadius: 6,
              border: `1px solid ${palette.accent}`,
              background: adding ? palette.g100 : palette.accent,
              color: adding ? palette.g500 : palette.white,
              fontFamily: 'inherit',
              fontWeight: 600,
              fontSize: 13,
              cursor: adding ? 'default' : 'pointer',
              alignSelf: 'flex-start',
            }}
            aria-label="Add walk-in"
          >
            <Plus size={14} />
            <span>{adding ? 'Adding…' : 'Add walk-in'}</span>
          </button>

          {addError ? (
            <div style={{ fontSize: 12, color: palette.red }}>{addError}</div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
